import { uniq } from 'lodash';
import React, { useContext } from 'react';
import { getUserSalesOrgs } from 'src/libs/auth';
import { DemiBold } from 'src/styles/fonts';
import styled from 'styled-components';
import { ReactComponent as AustriaFlag } from '../../assets/images/flags/austria.svg';
import { ReactComponent as BenefruxFlag } from '../../assets/images/flags/benefrux.svg';
import { ReactComponent as CanadaFlag } from '../../assets/images/flags/canada.svg';
import { ReactComponent as ChinaFlag } from '../../assets/images/flags/china.svg';
import { ReactComponent as EgyptFlag } from '../../assets/images/flags/egypt.svg';
import { ReactComponent as GermanyFlag } from '../../assets/images/flags/germany.svg';
import { ReactComponent as HongKongFlag } from '../../assets/images/flags/hong-kong.svg';
import { ReactComponent as IndiaFlag } from '../../assets/images/flags/india.svg';
import { ReactComponent as IndonesiaFlag } from '../../assets/images/flags/indonesia.svg';
import { ReactComponent as ItalyFlag } from '../../assets/images/flags/italy.svg';
import { ReactComponent as MacauFlag } from '../../assets/images/flags/macau.svg';
import { ReactComponent as MacroFlag } from '../../assets/images/flags/macro-distributor.svg';
import { ReactComponent as MalaysiaFlag } from '../../assets/images/flags/malaysia.svg';
import { ReactComponent as OmanFlag } from '../../assets/images/flags/oman.svg';
import { ReactComponent as PakistanFlag } from '../../assets/images/flags/pakistan.svg';
import { ReactComponent as PhilippinesFlag } from '../../assets/images/flags/philippines.svg';
import { ReactComponent as PolandFlag } from '../../assets/images/flags/poland.svg';
import { ReactComponent as RussiaFlag } from '../../assets/images/flags/russia.svg';
import { ReactComponent as SingaporeFlag } from '../../assets/images/flags/singapore.svg';
import { ReactComponent as SouthAfricaFlag } from '../../assets/images/flags/south-africa.svg';
import { ReactComponent as SpainFlag } from '../../assets/images/flags/spain.svg';
import { ReactComponent as SwitzerlandFlag } from '../../assets/images/flags/switzerland.svg';
import { ReactComponent as ThailandFlag } from '../../assets/images/flags/thailand.svg';
import { ReactComponent as TurkeyFlag } from '../../assets/images/flags/turkey.svg';
import { ReactComponent as UaeFlag } from '../../assets/images/flags/uae.svg';
import { ReactComponent as UKFlag } from '../../assets/images/flags/uk.svg';
import { ReactComponent as UsaFlag } from '../../assets/images/flags/usa.svg';
import { ReactComponent as SettingsIcon } from '../../assets/images/settings.svg';
import { useMenuState } from '../../hooks/use-menu-state';
import { ListOfCobDropDown } from '../../pages/list-of-lob/queries/lob-list';
import { RegionsContext } from '../../regions-context';
import { Grey2, MangoAnotherBlue, White, White1 } from '../../styles/colours';
import { DateFormatRadio } from './date-format-setting';
import { RegionsList } from './regions-list';
import { MenuPopUp, MenuToggleButton } from './toggle-menu';

const StyledMenuButton = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  border: 1px solid transparent;
  padding: 1rem;
  color: ${White};
  background: transparent;
  font-family: ${DemiBold};
`;

const StyledSettingsIconWrapper = styled.span`
  display: flex;
  margin-right: 0.5rem;
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  box-shadow: 0px -4px 4px 0px ${White};
  padding-top: 0.5rem;
`;
const CancelButton = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex: 1;

  cursor: pointer;
  justify-content: center;
  height: 3rem;
  line-height: 3rem;
  border-radius: 3px;
  border: 1px solid ${Grey2};
  margin: 0.75rem 0.75rem 0 0.75rem;

  background: ${White};
  color: ${MangoAnotherBlue};

  &:hover {
    background: ${White1};
  }
`;

const StyledHeading = styled.h4`
  margin: 0 0 0.75rem 0;
  color: ${Grey2};
`;

export type RegionName =
  | 'Austria'
  | 'Benefrux'
  | 'Canada'
  | 'China'
  | 'Germany'
  | 'Egypt'
  | 'Hong Kong'
  | 'India'
  | 'Indonesia'
  | 'International Marine'
  | 'Italy'
  | 'Macau'
  | 'Macro Distributor'
  | 'Malaysia'
  | 'Oman'
  | 'Pakistan'
  | 'Philippines'
  | 'Poland'
  | 'Russia'
  | 'Singapore'
  | 'South Africa'
  | 'Spain'
  | 'Switzerland'
  | 'Thailand'
  | 'Turkey'
  | 'UAE'
  | 'UK'
  | 'USA';

export const nonRestrictedRegionNames: RegionName[] = [
  'Austria',
  'Benefrux',
  'Canada',
  'China',
  'Germany',
  'Egypt',
  'Hong Kong',
  'India',
  'Indonesia',
  'International Marine',
  'Italy',
  'Macau',
  'Macro Distributor',
  'Malaysia',
  'Poland',
  'Russia',
  'Singapore',
  'South Africa',
  'Spain',
  'Switzerland',
  'Thailand',
  'Turkey',
  'UAE',
  'UK',
  'USA',
];

export type IRegions = {
  [key in RegionName]: { salesOrgs: string[]; flag: React.FunctionComponent };
};

export const ALL_REGIONS: IRegions = {
  Austria: { salesOrgs: ['AT01'], flag: AustriaFlag },
  Benefrux: { salesOrgs: ['BE01', 'LU01', 'NL01', 'FR01'], flag: BenefruxFlag },
  Canada: { salesOrgs: ['CA48'], flag: CanadaFlag },
  China: { salesOrgs: ['CN04'], flag: ChinaFlag },
  Germany: { salesOrgs: ['DE01'], flag: GermanyFlag },
  Egypt: { salesOrgs: ['EG12'], flag: EgyptFlag },
  'Hong Kong': { salesOrgs: ['HK02'], flag: HongKongFlag },
  India: { salesOrgs: ['IN02'], flag: IndiaFlag },
  Indonesia: { salesOrgs: ['ID01'], flag: IndonesiaFlag },
  'International Marine': { salesOrgs: ['SG88', 'US86'], flag: BenefruxFlag },
  Italy: { salesOrgs: ['IT15'], flag: ItalyFlag },
  Macau: { salesOrgs: ['MO02'], flag: MacauFlag },
  'Macro Distributor': { salesOrgs: ['NL29'], flag: MacroFlag },
  Malaysia: { salesOrgs: ['MY05', 'MY08'], flag: MalaysiaFlag },
  Oman: { salesOrgs: ['OM02'], flag: OmanFlag },
  Pakistan: { salesOrgs: ['PK01'], flag: PakistanFlag },
  Philippines: { salesOrgs: ['PH01'], flag: PhilippinesFlag },
  Poland: { salesOrgs: ['PL03'], flag: PolandFlag },
  Russia: { salesOrgs: ['RU05'], flag: RussiaFlag },
  Singapore: { salesOrgs: ['SG01'], flag: SingaporeFlag },
  'South Africa': { salesOrgs: ['ZA03'], flag: SouthAfricaFlag },
  Spain: { salesOrgs: ['ES01'], flag: SpainFlag },
  Switzerland: { salesOrgs: ['CH01'], flag: SwitzerlandFlag },
  Thailand: { salesOrgs: ['GB30'], flag: ThailandFlag },
  Turkey: { salesOrgs: ['TR04'], flag: TurkeyFlag },
  UAE: { salesOrgs: ['BM23'], flag: UaeFlag },
  UK: { salesOrgs: ['GB01'], flag: UKFlag },
  USA: { salesOrgs: ['US52'], flag: UsaFlag },
};

// transpose ALL_REGIONS to a map of comma-separated salesOrgs to region, e.g. {  'BE01,LU01,NL01,FR01': 'Benefrux', ... }
const SALES_ORG_REGION_MAP = (Object.keys(ALL_REGIONS) as RegionName[]).reduce<
  Record<string, RegionName>
>((dict, region) => {
  const { salesOrgs } = ALL_REGIONS[region];
  dict[salesOrgs.join(',')] = region;
  return dict;
}, {});

export const findRegionsForSalesOrgs = (salesOrgs: string[]): RegionName[] => {
  const regions = salesOrgs
    .map(org => {
      const matchedRow = Object.keys(SALES_ORG_REGION_MAP).find(orgs =>
        orgs.includes(org)
      );
      return matchedRow ? SALES_ORG_REGION_MAP[matchedRow] : undefined;
    })
    .filter(r => r !== undefined) as RegionName[];
  return uniq(regions);
};

const filterRegionsForSalesOrgs = (salesOrgs: string[]): IRegions => {
  const assignedRegions = findRegionsForSalesOrgs(salesOrgs);
  return (Object.keys(ALL_REGIONS) as RegionName[])
    .filter(r => assignedRegions.includes(r))
    .reduce<IRegions>((dict, r) => {
      dict[r] = ALL_REGIONS[r];
      return dict;
    }, ({} as unknown) as IRegions);
};

export const RegionsMenu = () => {
  const { regions, setRegions } = useContext(RegionsContext);
  const { displayed, setDisplayed, menuRef } = useMenuState();

  const orgs = getUserSalesOrgs();
  const availableRegions = filterRegionsForSalesOrgs(orgs);

  const setDateFormat = (event?: any): any => {
    localStorage.setItem('dateFormat', event.target.value);
    window.location.reload();
  };

  return (
    <div ref={menuRef}>
      <MenuToggleButton displayed={displayed} setDisplayed={setDisplayed}>
        <StyledMenuButton>
          <StyledSettingsIconWrapper>
            <SettingsIcon />
          </StyledSettingsIconWrapper>
          Settings
        </StyledMenuButton>
      </MenuToggleButton>
      <MenuPopUp displayed={displayed} setDisplayed={setDisplayed}>
        <>
          <StyledHeading>Date Format :</StyledHeading>
          <StyledHeading>
            <DateFormatRadio
              id="US"
              name="dateFormat"
              value="MM.DD.YYYY"
              changeHandler={setDateFormat}
            />
            <DateFormatRadio
              id="Others"
              name="dateFormat"
              value="DD.MM.YYYY"
              changeHandler={setDateFormat}
            />
          </StyledHeading>
          <StyledHeading>Class of Business :</StyledHeading>
          <StyledHeading>
            <ListOfCobDropDown />
          </StyledHeading>
          <StyledHeading>Sales org(s) :</StyledHeading>
          <RegionsList
            regions={availableRegions}
            selected={regions}
            setSelectedRegions={setRegions}
          />

          <Buttons>
            <CancelButton
              data-type="select-region-close-button"
              type="button"
              onClick={() => setDisplayed(false)}
            >
              Close
            </CancelButton>
          </Buttons>
        </>
      </MenuPopUp>
    </div>
  );
};
